import { useRouter } from 'next/router';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';

import { isInternalLink, isSpecialLink } from '@/contentful';

interface LinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  // Default behavior is to prefetch on hover
  // This prop allows the option to turn off prefetch on hover for specific links
  noPrefetchOnHover?: boolean;
}

export default function Link({
  href,
  onClick,
  onMouseEnter,
  children,
  noPrefetchOnHover = false,
  ...rest
}: LinkProps) {
  const router = useRouter();
  const isInternal = isInternalLink(href);
  const isSpecial = isSpecialLink(href);

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onClick) {
      onClick(e);
    }

    // Maintain the default anchor functionality if the link is external or user tries to load link in new tab
    if (!isInternal || e.metaKey || e.ctrlKey || e.shiftKey) {
      return;
    }

    // Use client side navigation for internal links
    e.preventDefault();
    router.push(href || '');
  };

  // Prefetch internal links on hover
  const handleMouseEnter = (e: MouseEvent<HTMLAnchorElement>) => {
    if (onMouseEnter) {
      onMouseEnter(e);
    }

    if (href && isInternal && !noPrefetchOnHover) {
      router.prefetch(href);
    }
  };

  // Open link in a new tab if it is an external web link
  const externalWebLinkProps =
    !isInternal && !isSpecial ? { target: '_blank', rel: 'noreferrer' } : {};

  return (
    // biome-ignore lint/correctness/noRestrictedElements: Automated migration to Biome
    <a
      href={href}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      {...externalWebLinkProps}
      {...rest}
    >
      {children}
    </a>
  );
}
